import React from "react";
import {
  PageContainer,
  PageHeader,
  PageContent,
} from "../../components/Layout";
import {
  Button,
  Modal,
  message as notify,
  Input,
  Space,
  Tag,
  message,
} from "antd";
import {
  PlusOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { MyTable, RowAction } from "../../components";
import { systemSetting as systemSettingApi } from "../../apis";
import { useSelector } from "react-redux";
import usePermission from "../../hooks/usePermission";
import styled from "styled-components";
import Form from "./Form";
import { useTranslate } from "../../hooks";

const List = () => {
  const { translate } = useTranslate();
  const myTableRef = React.useRef();
  const { checkPermission } = usePermission();
  const { isMobile } = useSelector((state) => state.general);
  const [action, setAction] = React.useState([]);
  const [query, setQuery] = React.useState("");
  const [filters, setFilters] = React.useState({
    query: "",
  });

  const columns = useHeader({
    translate,
    checkPermission,
    onAction: async (key, record) => {
      switch (key) {
        case "edit": {
          setAction(["update", record]);
          break;
        }
        default:
      }
    },
  });

  const onCancel = () => {
    setAction([]);
  };

  const onSubmit = async (data) => {
    try {
      if (action && action[0] === "update") {
        await systemSettingApi.update(action[1]?._id, data);
      } else {
        await systemSettingApi.create(data);
      }
      notify.success(translate("system.request.success", "Success!"));
      onCancel();
      myTableRef.current.reload();
    } catch (err) {
      if (err.message === "SYSTEM_SETTING_CODE_ALREADY_EXISTS") {
        message.error(translate("system.setting.list.code.exist", "このタイプの設定を作成ずみのため、二重作成できません！"));
        return;
      }
      message.error(err.message || translate("system.request.error", "Error!"));
    }
  };

  let timeout;

  React.useEffect(() => {
    if (timeout) clearTimeout(timeout);

    if (filters.query !== query)
      timeout = setTimeout(() => {
        setFilters((state) => ({ ...state, query: query }));
      }, 300);

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [query]);

  return (
    <PageContainer>
      <PageHeader
        title={translate("system.setting.list.pageheader", "システム設定")}
        extra={[
          <Button
            key={0}
            shape={isMobile && "circle"}
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setAction(["create"])}
          >
            {!isMobile && translate("system.btn.add", "Add")}
          </Button>,
        ]}
      />
      <PageFilter>
        <Space>
          <Input
            name="query"
            value={query}
            placeholder={translate("system.input.search", "Search...")}
            style={{ width: 250 }}
            onChange={(e) => setQuery(e.target.value)}
            prefix={<SearchOutlined />}
          />
        </Space>
      </PageFilter>
      <PageContent>
        <MyTable
          bordered={!isMobile}
          columns={columns}
          ref={myTableRef}
          filters={filters}
          loadData={systemSettingApi.list}
          rowKey={(record) => record._id}
        />
      </PageContent>

      <Modal
        title={
          action && action[0] === "create"
            ? translate("system.pagecontainer.model.title.add", "Add")
            : translate("system.pagecontainer.model.title.edit", "Edit")
        }
        open={action && action[0]}
        onCancel={onCancel}
        destroyOnClose
        footer={false}
      >
        {action && action[0] && (
          <Form onCancel={onCancel} action={action} onSubmit={onSubmit} />
        )}
      </Modal>
    </PageContainer>
  );
};

const useHeader = ({ onAction, translate }) => {
  return [
    {
      title : "No",
      width : 20,
      render: (r, i, count) => {
        return count + 1;
      },
    },
    {
      title : translate("system.setting.list.code", "コード"),
      render: (record) => {
        const CODE_LABELS = {
          PAYMENT_MODE            : "決済モード",
          "3DS_CHALLENGE_IND_TYPE": "3DS2.0認証チャレンジ要求タイプ",
        };
        return (
          <Tag color="blue">{CODE_LABELS[record.code] || record.code}</Tag>
        );
      },
    },
    {
      title : translate("system.setting.list.value", "値"),
      render: (record) => {
        const VALUE_LABELS = {
          PAYMENT_MODE: {
            GMOV1: "３DSなし",
            GMOV2: "３DSあり",
          },
          "3DS_CHALLENGE_IND_TYPE": {
            1: "認証必須",
            2: "認証必須ではない",
          },
        };
        const labels = VALUE_LABELS[record.code];
        return (labels && labels[record.value]) || record.value;
      },
    },
    {
      title : translate("system.useheader.title.action", "Action"),
      width : 100,
      render: (record) => {
        return (
          <RowAction
            actions={{
              edit: translate("system.action.edit", "Edit"),
            }}
            onClick={(key) => onAction(key, record)}
          />
        );
      },
    },
  ];
};

const PageFilter = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 20px 0;
`;

export default List;
